import { useEffect, useState } from 'react';
import { Box, Typography, Paper, Grid, CircularProgress, MenuItem, TextField, IconButton, Divider, Button } from '@mui/material';
import { Add, Remove, ArrowBack } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { Snackbar, Alert } from '@mui/material';
import axios from 'axios';

const API_URL = `http://192.168.1.248:3103/api`;

export default function ViewOrderPage({ loggedInUser, companyData }) {
    const userId = loggedInUser?.userId;
    const dateTime = new Date().toISOString().slice(0, 19).replace('T', ' ');
    const navigate = useNavigate();  // 🚀 add navigation
    const { SOAutoID } = useParams();
    const [loading, setLoading] = useState(true);
    const [orderItems, setOrderItems] = useState([]);
    const [colorOptions, setColorOptions] = useState([]);
    const [sizeOptions, setSizeOptions] = useState([]);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success'); // or 'error'

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [orderRes, colorRes, sizeRes] = await Promise.all([
                    axios.get(`${API_URL}/getDetails/${SOAutoID}`),
                    axios.get(`${API_URL}/colormaster`),
                    axios.get(`${API_URL}/sizemaster`),
                ]);
                console.log(orderRes.data.items)
                const enriched = (orderRes.data.items || []).map(item => ({
                    ...item,
                    selectedSize: item.ItemSize || '',
                    selectedColor: item.ItemColor || '',
                    selectedQty: item.Qty || 1,
                    originalSize: item.ItemSize || '',
                    originalColor: item.ItemColor || '',
                    originalQty: item.Qty || 1,
                }));

                setOrderItems(enriched);
                setColorOptions(colorRes.data || []);
                setSizeOptions(sizeRes.data || []);
            } catch (e) {
                console.error('Fetch failed:', e);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [SOAutoID]);

    const onChange = (idx, field, value) => {
        setOrderItems(prev => {
            const updated = prev.map((item, i) => i === idx ? { ...item, [field]: value } : item);
            return updated;
        });
    };

    const hasChanged = item => item.selectedSize !== item.originalSize || item.selectedColor !== item.originalColor || item.selectedQty !== item.originalQty;

    const handleUpdateOrder = async (product) => {
        const rate = +product.saleRate || 0;
        const qty = +product.selectedQty || 0;
        const discPer = +product.Disc || 0;
        const gstRate = +(product.taxName ?? product.IGST ?? 0);
        const isIGST = +product.CGST === 0 && +product.SGST === 0;

        const exclTotal = rate * qty;
        const discAmt = exclTotal * (discPer / 100);
        const taxableAmt = exclTotal - discAmt;

        const IGSTAmt = isIGST ? (taxableAmt * gstRate) / 100 : 0;
        const CGSTAmt = isIGST ? 0 : (taxableAmt * (gstRate / 2)) / 100;
        const SGSTAmt = isIGST ? 0 : (taxableAmt * (gstRate / 2)) / 100;

        const detailPayload = {
            SODtsID: product.SODtsID,
            ItemSize: product.selectedSize?.id ?? product.selectedSize,  // Use ID
            ItemColor: product.selectedColor?.id ?? product.selectedColor, // Use ID
            Qty: qty,
            DiscAmt: discAmt.toFixed(2),
            CGSTAmt: CGSTAmt.toFixed(2),
            SGSTAmt: SGSTAmt.toFixed(2),
            IGSTAmt: IGSTAmt.toFixed(2),
        };

        // ===== Recalculate master totals from all order items =====
        let basicAmt = 0, totalDiscAmt = 0;
        let cgstAmt = 0, sgstAmt = 0, igstAmt = 0;

        orderItems.forEach(prod => {
            const r = +prod.saleRate || 0;
            const q = +(prod.selectedQty ?? prod.Qty) || 0;
            const total = r * q;

            const dPer = +prod.Disc || 0;
            const dAmt = total * (dPer / 100);
            const tAmt = total - dAmt;

            const gst = +(prod.taxName ?? prod.IGST ?? 0);
            const isIGST = +prod.CGST === 0 && +prod.SGST === 0;

            basicAmt += total;
            totalDiscAmt += dAmt;

            if (isIGST) {
                igstAmt += tAmt * gst / 100;
            } else {
                const halfGst = gst / 2;
                cgstAmt += tAmt * halfGst / 100;
                sgstAmt += tAmt * halfGst / 100;
            }
        });

        const billAmt = basicAmt - totalDiscAmt + cgstAmt + sgstAmt + igstAmt;
        const roundOff = +(Math.round(billAmt) - billAmt).toFixed(2);

        const masterPayload = {
            SOAutoID: SOAutoID,
            BasicAmt: basicAmt.toFixed(2),
            Disc: discPer,
            DiscAmt: totalDiscAmt.toFixed(2),
            CGST: cgstAmt > 0 ? (gstRate / 2).toFixed(2) : "0.00",
            CGSTAmt: cgstAmt.toFixed(2),
            SGST: sgstAmt > 0 ? (gstRate / 2).toFixed(2) : "0.00",
            SGSTAmt: sgstAmt.toFixed(2),
            IGST: igstAmt > 0 ? gstRate.toFixed(2) : "0.00",
            IGSTAmt: igstAmt.toFixed(2),
            RoundOff: Math.abs(roundOff).toFixed(2),
            BillAmt: Math.round(billAmt).toFixed(2),
            editUserId: userId,
            editUserDateTime: dateTime
        };

        const fullPayload = {
            detail: detailPayload,
            master: masterPayload
        };

        console.log("Payload to be sent:", fullPayload);

        try {
            await axios.post(`${API_URL}/update-saleorder`, fullPayload);
            setSnackbarMessage("Order updated successfully");
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
        } catch (err) {
            console.error("Update failed:", err);
            setSnackbarMessage("Update failed");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        }
    };

    return (
        <Box p={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5" fontWeight={600}>
                    📄 Manage Order – #{SOAutoID}
                </Typography>
                <IconButton onClick={() => navigate(-1)}>
                    <ArrowBack />
                </IconButton>
            </Box>

            {loading ? (
                <Box textAlign="center" py={5}>
                    <CircularProgress />
                </Box>
            ) : orderItems.length === 0 ? (
                <Typography color="text.secondary"> No items found for this order. </Typography>
            ) : orderItems.map((product, idx) => {
                const rate = product.saleRate || 0;
                const gst = product.taxName || product.igst || 0;
                const qty = product.selectedQty;
                const exclTotal = rate * qty;
                const inclTotal = exclTotal * (1 + gst / 100);

                return (
                    <Paper key={idx} elevation={2} sx={{ mb: 3, p: 2, bgcolor: '#F1F5F9' }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={8}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    {product.itemCode}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {product.description}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                                    Unit: {product.Unit} | HSN: {product.HSN}
                                </Typography>

                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={6} sm={4}>
                                        <Typography variant="caption">Size</Typography>
                                        <TextField fullWidth select size="small" value={product.selectedSize} onChange={e => onChange(idx, 'selectedSize', +e.target.value)} >
                                            {sizeOptions.map(o => (
                                                <MenuItem key={o.sizeId} value={o.sizeId}>
                                                    {o.itemSize}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={6} sm={4}>
                                        <Typography variant="caption">Color</Typography>
                                        <TextField fullWidth select size="small" value={product.selectedColor} onChange={e => onChange(idx, 'selectedColor', +e.target.value)} >
                                            {colorOptions.map(o => (
                                                <MenuItem key={o.colorId} value={o.colorId}>
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <Box
                                                            sx={{
                                                                width: 14, height: 14, borderRadius: '50%',
                                                                bgcolor: o.colorCode, border: '1px solid #ccc'
                                                            }}
                                                        />
                                                        {o.itemColor}
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="caption">Quantity</Typography>
                                        <Box display="flex" alignItems="center">
                                            <IconButton size="small" onClick={() => onChange(idx, 'selectedQty', Math.max(product.minOrderQty || 1, parseInt(qty || 0) - 1))} disabled={qty <= (product.minOrderQty || 1)} >
                                                <Remove fontSize="small" />
                                            </IconButton>
                                            <TextField size="small" value={qty} onChange={e => onChange(idx, 'selectedQty', Math.max(product.minOrderQty || 1, +e.target.value))} sx={{ width: 60, mx: 1, textAlign: 'center' }} inputProps={{ style: { textAlign: 'center' } }} />
                                            <IconButton size="small" onClick={() => onChange(idx, 'selectedQty', parseInt(qty || 0) + 1)} >
                                                <Add fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </Grid>
                                </Grid>

                                <Divider sx={{ my: 2 }} />

                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Typography variant="caption">Rate</Typography>
                                        <Typography variant="body2"> ₹{rate} × {qty} = ₹{(exclTotal).toFixed(2)} </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption">GST ({gst}%)</Typography>
                                        <Typography variant="body2"> ₹{((exclTotal * gst) / 100).toFixed(2)} </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={12} md={4} display="flex" justifyContent="flex-end" alignItems="center">
                                <Box textAlign="right">
                                    <Typography variant="caption" color="text.secondary"> Total (incl. GST) </Typography>
                                    <Typography variant="h6" fontWeight={700} color="primary"> ₹{inclTotal.toFixed(2)} </Typography>
                                </Box>
                            </Grid>
                        </Grid>

                        {hasChanged(product) && (
                            <Box mt={4} textAlign="center">
                                <Button variant="contained" color="primary" size="large" sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none', px: 4 }} onClick={() => handleUpdateOrder(product)} >
                                    Update Order
                                </Button>
                            </Box>
                        )}
                    </Paper>
                );
            })}

            <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)} anchorOrigin={{ vertical: 'top', horizontal: 'right' }} >
                <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
}